/**
 * 預約更新（批192）的純邏輯：維護時段摘要、狀態標籤、站台排序、時區換算、錯誤碼中文。
 *
 * 只放可單測的純函式；畫面與呼叫在 `HostDetailView.vue`。
 * 時間一律以後端回的 `timezone`（IPO_LS_HOSTING_TIMEZONE）顯示與輸入，不吃瀏覽器本地時區。
 */
import type { UpgradeRun, UpgradeRunItem, UpgradeWindow } from '@/api/hosting'
import { errorDetailOf } from '@/lib/apiErrors'

/** ISO 星期：1＝週一 … 7＝週日。 */
export const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: '一' },
  { value: 2, label: '二' },
  { value: 3, label: '三' },
  { value: 4, label: '四' },
  { value: 5, label: '五' },
  { value: 6, label: '六' },
  { value: 7, label: '日' },
]

/** 「每週三、六」；全選＝「每天」；空＝空字串。 */
export function weekdaysText(days: number[]): string {
  const set = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b)
  if (!set.length) return ''
  if (set.length === 7) return '每天'
  return `每週${set.map((d) => WEEKDAYS[d - 1].label).join('、')}`
}

/** 維護時段一列摘要。`null`／沒有星期＝「未設定」；停用但保留設定＝「已停用（…）」。 */
export function windowSummary(w: UpgradeWindow | null | undefined, timezone: string): string {
  if (!w || !w.weekdays.length) return '未設定'
  const base = `${weekdaysText(w.weekdays)} ${w.time}（${timezone}）${w.include_plugins ? '，含擴充套件' : ''}`
  return w.enabled ? base : `已停用（${base}）`
}

/** 預設維護時段（第一次設定時的表單初值）。 */
export function defaultWindow(): UpgradeWindow {
  return { enabled: true, weekdays: [3, 6], time: '03:30', include_plugins: true, max_minutes: 180, site_order: [] }
}

// ---------------------------------------------------------------------------
// 狀態標籤
// ---------------------------------------------------------------------------

export type PillClass = 'active' | 'pending' | 'revoked' | ''

export const ITEM_STATUS: Record<UpgradeRunItem['status'], { label: string; cls: PillClass }> = {
  pending: { label: '等待中', cls: 'pending' },
  running: { label: '更新中', cls: 'active' },
  done: { label: '完成', cls: 'active' },
  done_warn: { label: '完成但擴充套件有問題', cls: 'pending' },
  failed: { label: '失敗已略過', cls: 'revoked' },
  skipped: { label: '略過', cls: '' },
  skipped_deadline: { label: '逾時略過', cls: 'revoked' },
  canceled: { label: '已取消', cls: '' },
}

export const RUN_STATUS: Record<UpgradeRun['status'], { label: string; cls: PillClass }> = {
  scheduled: { label: '已預約', cls: 'pending' },
  running: { label: '進行中', cls: 'active' },
  done: { label: '已完成', cls: 'active' },
  partial: { label: '部分完成', cls: 'pending' },
  paused: { label: '已暫停，需人工處理', cls: 'revoked' },
  canceled: { label: '已取消', cls: '' },
  missed: { label: '錯過（未執行）', cls: 'revoked' },
}

export const RUN_SOURCE_LABEL: Record<UpgradeRun['source'], string> = {
  once: '單次預約',
  window: '維護時段',
  batch: '手動批次',
}

export function itemStatus(s: string): { label: string; cls: PillClass } {
  return ITEM_STATUS[s as UpgradeRunItem['status']] ?? { label: s, cls: '' }
}
export function runStatus(s: string): { label: string; cls: PillClass } {
  return RUN_STATUS[s as UpgradeRun['status']] ?? { label: s, cls: '' }
}

/** 「略過」類的原因常放在 detail；其餘狀態 detail 也一併顯示（錯誤碼或訊息）。 */
export function itemResultText(it: UpgradeRunItem): string {
  const parts: string[] = []
  if (it.from || it.to) {
    parts.push(it.from && it.to && it.from !== it.to ? `${it.from} → ${it.to}` : (it.to ?? it.from ?? ''))
  }
  if (it.plugins) {
    const { upgraded, failed, skipped } = it.plugins
    const seg = [`升級 ${upgraded}`]
    if (failed) seg.push(`失敗 ${failed}`)
    if (skipped) seg.push(`略過 ${skipped}`)
    parts.push(`擴充套件：${seg.join('、')}`)
  }
  if (it.detail) parts.push(it.detail)
  return parts.filter(Boolean).join('｜')
}

/** 這個 run 還沒結束（要留在卡片最上面、可取消）。 */
export function isRunActive(r: UpgradeRun): boolean {
  return r.status === 'scheduled' || r.status === 'running' || r.status === 'paused'
}

/** 進度「完成 N／共 M」。 */
export function runProgress(r: UpgradeRun): { done: number; total: number } {
  const finished = new Set(['done', 'done_warn', 'failed', 'skipped', 'skipped_deadline', 'canceled'])
  return { done: r.items.filter((i) => finished.has(i.status)).length, total: r.items.length }
}

/** 全域佇列位置文字。 */
export function positionText(r: UpgradeRun): string {
  return r.position != null ? `全域佇列第 ${r.position} 個` : ''
}

// ---------------------------------------------------------------------------
// 站台排序（↑↓）
// ---------------------------------------------------------------------------

/** 把 index 的項目往上（-1）或往下（+1）移一格；越界回原陣列的拷貝。 */
export function moveItem<T>(list: readonly T[], index: number, dir: -1 | 1): T[] {
  const out = [...list]
  const j = index + dir
  if (index < 0 || index >= out.length || j < 0 || j >= out.length) return out
  ;[out[index], out[j]] = [out[j], out[index]]
  return out
}

/** 依已存順序排 `ids`：存過的在前（依存的順序），新站（不在順序裡）接在後面保持原序；已不存在的丟掉。 */
export function orderByPreferred(ids: readonly string[], preferred: readonly string[]): string[] {
  const have = new Set(ids)
  const head = preferred.filter((x, i) => have.has(x) && preferred.indexOf(x) === i)
  const seen = new Set(head)
  return [...head, ...ids.filter((x) => !seen.has(x))]
}

// ---------------------------------------------------------------------------
// 時區換算（datetime-local 字串 <-> ISO）
// ---------------------------------------------------------------------------

function partsIn(date: Date, timeZone: string): Record<string, number> {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const out: Record<string, number> = {}
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value)
  }
  return out
}

const p2 = (n: number) => String(n).padStart(2, '0')

/** ISO（或 Date）→ `YYYY-MM-DDTHH:mm`（該時區的牆上時間，給 datetime-local）。 */
export function toTzInput(iso: string | Date, timeZone: string): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(d.getTime())) return ''
  const p = partsIn(d, timeZone)
  return `${p.year}-${p2(p.month)}-${p2(p.day)}T${p2(p.hour)}:${p2(p.minute)}`
}

/** `YYYY-MM-DDTHH:mm`（該時區牆上時間）→ ISO（UTC）。格式錯誤回 null。 */
export function tzInputToIso(local: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local)
  if (!m) return null
  const [y, mo, d, h, mi] = m.slice(1).map(Number)
  const asUtc = Date.UTC(y, mo - 1, d, h, mi)
  // 兩輪逼近：先用猜測值算偏移，再用修正後的時間重算一次（跨夏令時間界線也穩）。
  let guess = asUtc
  for (let i = 0; i < 2; i++) {
    const p = partsIn(new Date(guess), timeZone)
    const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute)
    guess += asUtc - wall
  }
  return new Date(guess).toISOString()
}

/** 顯示用：`YYYY-MM-DD HH:mm`（該時區）；沒有值回「—」。 */
export function fmtInTz(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—'
  const s = toTzInput(iso, timeZone)
  return s ? s.replace('T', ' ') : iso
}

/** 單次預約的預設時間：現在起 60 分鐘後，進位到 10 分鐘（至少滿足後端「≥ 現在＋5 分鐘」）。 */
export function defaultRunAtInput(timeZone: string, now: Date = new Date()): string {
  const ms = 10 * 60_000
  const t = new Date(Math.ceil((now.getTime() + 60 * 60_000) / ms) * ms)
  return toTzInput(t, timeZone)
}

/** 預約時間是否落在後端允許範圍（≥ 現在＋5 分鐘、≤ 30 天）。 */
export function runAtProblem(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return '請填寫預約時間。'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '預約時間格式不正確。'
  if (t < now.getTime() + 5 * 60_000) return '預約時間至少要在 5 分鐘之後。'
  if (t > now.getTime() + 30 * 86_400_000) return '預約時間最多只能排到 30 天內。'
  return null
}

// ---------------------------------------------------------------------------
// 錯誤碼中文
// ---------------------------------------------------------------------------

/** 預約更新相關端點的錯誤 → 中文；認不得回 null（呼叫端退回後端 message）。 */
export function upgradeErrorText(e: unknown, agentMinVersion?: string | null): string | null {
  const d = errorDetailOf(e)
  switch (d?.code) {
    case 'schedule_exists':
      return '這台主機已經有一筆待執行的單次預約。請先取消它，或等它執行完再排新的。'
    case 'agent_too_old':
      return `這台主機的 agent 版本太舊，不支援預約更新${agentMinVersion ? `（需升級到 ${agentMinVersion} 以上）` : ''}。`
    case 'run_at_out_of_range':
      return '預約時間必須在 5 分鐘之後、30 天之內。'
    case 'mixed_batch':
      return null
    default:
      return null
  }
}
